import React, { useEffect, useId, useRef, useState } from 'react';

import styles from './SavedReason.module.scss';

export type LoadReason = () => Promise<{ data: { reason: string | null; approver_name?: string | null } }>;

const SavedReason = ({ loadReason, canReadReason = false, inline = false }: { loadReason: LoadReason; canReadReason?: boolean; inline?: boolean }) => {
  const [reason, setReason] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const requestId = useRef(0);
  const titleId = useId();

  useEffect(() => () => { requestId.current++; }, []);

  const openReason = async () => {
    if (!canReadReason) return;
    const currentRequest = ++requestId.current;
    setReason(null);
    setApproverName(null);
    setError(false);
    setLoading(true);
    dialogRef.current?.showModal();
    try {
      const response = await loadReason();
      if (currentRequest === requestId.current) {
        setReason(response.data.reason);
        setApproverName(response.data.approver_name ?? null);
      }
    } catch {
      if (currentRequest === requestId.current) setError(true);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Keep the surrounding StackView open while the browser dismisses this dialog.
        event.stopPropagation();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);
    return () => dialog.removeEventListener('keydown', handleKeyDown);
  }, [canReadReason]);

  if (!canReadReason) {
    return null;
  }

  return (
    <div className={inline ? styles.inline : styles.reason}>
      <button
        type="button"
        className={styles.link}
        aria-haspopup="dialog"
        onClick={openReason}
      >
        Reason
      </button>
      <dialog
        ref={dialogRef}
        className={styles.popup}
        aria-labelledby={titleId}
      >
        <header className={styles.header}>
          <span className={styles.eyebrow}>Assignment details</span>
          <h2 id={titleId} className={styles.title}>Reason</h2>
        </header>
        <div className={styles.content}>
          {loading ? <span role="status">Loading reason…</span> : error ? (
            <span role="alert">Unable to load reason. Please try again.</span>
          ) : (
            <>
              <section className={styles.reasonCard} aria-label="Reason text">
                <span className={styles.label}>Reason</span>
                <div className={styles.reasonText}>{reason?.trim() ? reason : 'No reason recorded.'}</div>
              </section>
              {approverName?.trim() && (
                <div className={styles.approver}>
                  <span className={styles.approverLabel}>Approver</span>
                  <span className={styles.approverName}>{approverName}</span>
                </div>
              )}
            </>
          )}
        </div>
        <footer className={styles.actions}>
          <button type="button" className={styles.close} onClick={() => dialogRef.current?.close()}>
            Close
          </button>
        </footer>
      </dialog>
    </div>
  );
};

export default SavedReason;
